import { useEffect, useState, type FormEvent } from "react";
import { createResource } from "./api.js";

type Session = { tenantId: string; token: string; csrf: string };
type Provider = {
  channel: "email" | "sms";
  enabled: boolean;
  version: number;
  host?: string;
  port?: number;
  from?: string;
  username?: string;
  endpoint?: string;
  token_header?: string;
  has_secret: boolean;
};
type Delivery = {
  id: string;
  channel: string;
  purpose: string;
  recipient_masked: string;
  status: string;
  attempts: number;
  last_error_code: string | null;
  subject?: string;
  text?: string;
};
export function NotificationProviders({ session }: { session: Session }) {
  const [data, setData] = useState<{
      providers: Provider[];
      deliveries: Delivery[];
    }>(),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [busy, setBusy] = useState(false);
  async function load() {
    try {
      const response = await fetch(
        `/api/v1/notifications/providers?tenant_id=${encodeURIComponent(session.tenantId)}`,
        {
          headers: session.token
            ? { authorization: `Bearer ${session.token}` }
            : {},
          cache: "no-store",
        },
      );
      if (!response.ok)
        throw new Error(
          response.status === 403
            ? "You do not have permission to view delivery providers."
            : "Unable to load delivery providers.",
        );
      setData(await response.json());
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Unable to load providers",
      );
    }
  }
  useEffect(() => {
    void load();
  }, [session.tenantId, session.token]);
  async function save(
    event: FormEvent<HTMLFormElement>,
    channel: "email" | "sms",
  ) {
    event.preventDefault();
    if (!data) return;
    const form = event.currentTarget,
      values = new FormData(form),
      previous = data.providers.find((item) => item.channel === channel);
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const payload = {
        channel,
        enabled: values.has("enabled"),
        expected_version: previous?.version ?? 0,
        ...(channel === "email"
          ? {
              host: String(values.get("host")),
              port: Number(values.get("port")),
              from: String(values.get("from")),
              username: String(values.get("username")),
              ...(values.get("password")
                ? { password: String(values.get("password")) }
                : {}),
            }
          : {
              endpoint: String(values.get("endpoint")),
              token_header: String(values.get("token_header")),
              ...(values.get("body_template")
                ? { body_template: String(values.get("body_template")) }
                : {}),
              ...(values.get("token")
                ? { token: String(values.get("token")) }
                : {}),
            }),
      };
      await createResource(
        "/api/v1/notifications/providers",
        session.tenantId,
        session.token,
        payload,
        session.csrf,
        "PUT",
      );
      form
        .querySelectorAll<HTMLInputElement>('input[type="password"]')
        .forEach((input) => {
          input.value = "";
        });
      setNotice(`${channel === "email" ? "Email" : "SMS"} provider saved.`);
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Unable to save provider",
      );
    } finally {
      setBusy(false);
    }
  }
  const email = data?.providers.find((item) => item.channel === "email"),
    sms = data?.providers.find((item) => item.channel === "sms");
  async function retry(id: string) {
    setBusy(true);
    setError("");
    try {
      await createResource(
        `/api/v1/notifications/deliveries/${id}/retry`,
        session.tenantId,
        session.token,
        {},
        session.csrf,
      );
      setNotice("Delivery queued for another attempt.");
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Unable to retry delivery",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="panel notification-rules">
      <header>
        <div>
          <p className="eyebrow">Delivery</p>
          <h3>Email and SMS providers</h3>
        </div>
        <button
          className="secondary"
          disabled={busy}
          onClick={() => void load()}
        >
          Refresh
        </button>
      </header>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="form-success" role="status">
          {notice}
        </p>
      )}
      {!data ? (
        <p>Loading providers…</p>
      ) : (
        <>
          <form
            key={`email-${email?.version ?? 0}`}
            className="credential-policy-form"
            onSubmit={(event) => void save(event, "email")}
          >
            <h3>Email · VNS SMTP</h3>
            <div className="field-pair">
              <label>
                Outgoing server
                <input
                  name="host"
                  defaultValue={email?.host ?? "smtp.vns.ae"}
                  required
                />
              </label>
              <label>
                TLS connection
                <select name="port" defaultValue={email?.port ?? 587}>
                  <option value="587">587 · required STARTTLS</option>
                  <option value="465">465 · implicit TLS</option>
                </select>
              </label>
              <label>
                Sender email
                <input
                  name="from"
                  type="email"
                  defaultValue={email?.from ?? ""}
                  required
                />
              </label>
              <label>
                SMTP username
                <input
                  name="username"
                  autoComplete="off"
                  defaultValue={email?.username ?? ""}
                />
              </label>
              <label>
                Password
                <input
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  placeholder={
                    email?.has_secret
                      ? "Saved password retained when blank"
                      : "SMTP password"
                  }
                />
              </label>
              <label className="policy-toggle">
                <input
                  name="enabled"
                  type="checkbox"
                  defaultChecked={email?.enabled ?? false}
                />
                <span>Enable email delivery</span>
              </label>
            </div>
            <footer>
              <p>
                Configure your VNS sender and authentication. Messages wait in a
                durable queue.
              </p>
              <button className="primary" disabled={busy}>
                Save email provider
              </button>
            </footer>
          </form>
          <form
            key={`sms-${sms?.version ?? 0}`}
            className="credential-policy-form"
            onSubmit={(event) => void save(event, "sms")}
          >
            <h3>SMS · configurable HTTPS API</h3>
            <div className="field-pair">
              <label>
                HTTPS endpoint
                <input
                  name="endpoint"
                  type="url"
                  defaultValue={sms?.endpoint ?? ""}
                  placeholder="https://sms.example.com/messages"
                  required
                />
              </label>
              <label>
                Token header
                <input
                  name="token_header"
                  defaultValue={sms?.token_header ?? "Authorization"}
                  pattern="Authorization|X-[A-Za-z0-9-]+"
                  required
                />
              </label>
              <label>
                API token
                <input
                  name="token"
                  type="password"
                  autoComplete="new-password"
                  placeholder={
                    sms?.has_secret
                      ? "Saved token retained when blank"
                      : "Optional API token"
                  }
                />
              </label>
              <label className="policy-toggle">
                <input
                  name="enabled"
                  type="checkbox"
                  defaultChecked={sms?.enabled ?? false}
                />
                <span>Enable SMS delivery</span>
              </label>
            </div>
            <label>
              JSON request template
              <textarea
                name="body_template"
                rows={4}
                defaultValue={
                  sms
                    ? ""
                    : '{"to":"{{recipient}}","message":"{{message}}","reference":"{{idempotency_key}}"}'
                }
                placeholder={
                  sms
                    ? "Saved template retained when blank"
                    : "JSON request body"
                }
                required={!sms}
              />
            </label>
            <p>
              Use {"{{recipient}}"}, {"{{message}}"} and optionally{" "}
              {"{{idempotency_key}}"} inside JSON values. Leave blank to retain
              the saved template. Authorization uses a Bearer token; X- headers
              use the token directly. The API must accept POST JSON and report
              acceptance with a 2xx response.
            </p>
            <footer>
              <span />
              <button className="primary" disabled={busy}>
                Save SMS provider
              </button>
            </footer>
          </form>
          <h3>Recent deliveries</h3>
          {data.deliveries.length === 0 ? (
            <p>No delivery attempts yet.</p>
          ) : (
            <div className="notification-rule-list">
              {data.deliveries.map((item) => (
                <article key={item.id}>
                  <div>
                    <strong>
                      {item.subject ??
                        `${item.channel} · ${item.recipient_masked}`}
                    </strong>
                    <small>
                      {item.purpose.replaceAll("_", " ")} · attempts{" "}
                      {item.attempts}
                      {item.last_error_code ? ` · ${item.last_error_code}` : ""}
                    </small>
                    {item.text && <p>{item.text}</p>}
                  </div>
                  <span>{item.status.replaceAll("_", " ")}</span>
                  {item.status === "dead_letter" && (
                    <button
                      className="secondary"
                      disabled={busy}
                      onClick={() => void retry(item.id)}
                    >
                      Retry
                    </button>
                  )}
                </article>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
